import { useSubscription } from "@apollo/client";
import { captureException } from "@sentry/react";
import { createElement } from "react";
import type { ChatMessage } from "../ChatBox/types";
import { AnswerModel, ANSWER_MODEL_ID, placeAnswerModel } from "./AnswerModel";
import { useRangerChatStore } from "./chatStore";
import { CONTEXTUAL_CHAT_SUBSCRIPTION } from "./graphql";
import { applyStreamEventToHistory } from "./streamEventToHistory";
import { notifyTurnComplete } from "./chatNotifications";

/** Labels the answer to the latest question with the model that wrote it. A
 * message queued meanwhile is not in the history yet, so the latest user
 * message is the one this `stop` answers. */
export function withAnswerModel(history: ChatMessage[], modelKey: string, effort?: string | null): ChatMessage[] {
  const asked = history.findLast(m => m.type === "user");
  if (!asked?.id) return history;
  const label: ChatMessage = {
    type: "agent",
    id: `${ANSWER_MODEL_ID}${asked.id}`,
    data: { type: "react", message: createElement(AnswerModel, { modelKey, effort }) },
  };
  return placeAnswerModel(history, asked.id, label);
}

/**
 * One subscription per slot, keyed by sessionId so React never unmounts /
 * remounts when the user switches active conversations. This is critical:
 * an unmount/remount race could drop the first event (which carries the
 * server-side convId), leaving a brand-new conversation with
 * conversationId=null forever — invisible to the sidebar merge logic.
 *
 * Mounted for every slot the user might still receive events for:
 * - the active slot (so a freshly-sent message has a subscription already
 *   in place — no setup race), and
 * - any slot whose status is "pending" or "generating" (background chats
 *   that started before the user navigated away).
 *
 * Writes go through setSlotChatHistory(sessionId, ...). For the active slot,
 * the user sees the stream via selectChatHistory; for background slots, the
 * chatHistory is preserved on the slot until they switch back.
 */
export function SlotWatcher({ sessionId }: { sessionId: string }) {
  const patchSlot = useRangerChatStore(s => s.patchSlot);
  const setSlotStatus = useRangerChatStore(s => s.setSlotStatus);
  const setSlotChatHistory = useRangerChatStore(s => s.setSlotChatHistory);
  useSubscription(CONTEXTUAL_CHAT_SUBSCRIPTION, {
    variables: { sessionId },
    onError(error) {
      // Reported either way: a subscription that errored may not come back
      // for this slot, and its next message would then fail with "hasn't
      // started" and no other trace of why.
      captureException(error, { tags: { feature: "rangerChat:subscriptionError" } });
      // Nothing running in this slot: no turn to end, so no bubble.
      if (useRangerChatStore.getState().slots.get(sessionId)?.status === "done") return;
      setSlotChatHistory(sessionId, prev => [
        ...prev,
        {
          type: "agent" as const,
          data: { type: "markdown" as const, message: "Something went wrong. Please try again." },
        },
      ]);
      patchSlot(sessionId, { status: "done", shouldStop: false });
      notifyTurnComplete({
        sessionId,
        outcome: "error",
        onActivate: () => useRangerChatStore.getState().switchToSlot(sessionId),
      });
    },
    onData({ data }) {
      // Any event proves the turn reached the server and is still alive;
      // dispatchMessage's watchdogs read both (reset when a message is sent).
      patchSlot(sessionId, { streamStarted: true, lastStreamEventAt: Date.now() });
      // A heartbeat says the turn is alive (recorded just above) and how often
      // to expect the next one.
      if (data.data?.rangerChatSubscription.messageStream.event === "heartbeat") {
        const heartbeatMs = Number(data.data.rangerChatSubscription.messageStream.message);
        if (heartbeatMs > 0) patchSlot(sessionId, { heartbeatMs });
        return;
      }
      // start event fires immediately on the server before any DB/LLM work —
      // transition to generating right away so the UI shows activity.
      // Only advance from "pending" — if the slot is already "done" (e.g. the
      // mutation threw after publishing start), don't flip it back to generating.
      if (data.data?.rangerChatSubscription.messageStream.event === "start") {
        const current = useRangerChatStore.getState().slots.get(sessionId);
        if (current?.status === "pending") setSlotStatus(sessionId, "generating");
        // New turn: the previous turn's compaction state is history, even
        // when it ended without a `stop` (a failure or a lost stream).
        patchSlot(sessionId, {
          compressionCount: 0,
          isCompressing: false,
          isEditingContext: false,
          retryNote: undefined,
        });
        return;
      }
      const streamConvId = data.data?.rangerChatSubscription.conversationId;
      if (streamConvId) {
        const existing = useRangerChatStore.getState().slots.get(sessionId);
        if (existing && existing.conversationId !== streamConvId) {
          patchSlot(sessionId, { conversationId: streamConvId, status: "generating" });
        } else if (existing && existing.status !== "generating") {
          setSlotStatus(sessionId, "generating");
        }
      }
      const event = data.data?.rangerChatSubscription.messageStream.event;
      if (event === "stop") {
        // The stop event carries the complete answer; applying it replaces the
        // text accumulated from deltas, so a dropped chunk cannot leave the
        // transcript short.
        const stopping = useRangerChatStore.getState().slots.get(sessionId);
        if (!stopping?.shouldStop && data.data?.rangerChatSubscription.answer) {
          setSlotChatHistory(sessionId, prev => applyStreamEventToHistory(prev, data));
        }
        const answeredBy = data.data?.rangerChatSubscription.model;
        if (answeredBy) {
          const effort = data.data?.rangerChatSubscription.effort;
          setSlotChatHistory(sessionId, prev => withAnswerModel(prev, answeredBy, effort));
        }
        // Use setSlotStatus (not patchSlot) so the "done" transition records
        // the conversation in completedConversations — that side effect drives
        // the sidebar green checkmark. Clear the transient activity flags too.
        setSlotStatus(sessionId, "done");
        patchSlot(sessionId, {
          isCompressing: false,
          isEditingContext: false,
          compressionCount: 0,
          retryNote: undefined,
        });
        // This watcher is mounted for background slots too, so a chat the user
        // navigated away from still announces itself. No-op while the tab has
        // focus; the user can already see it finish.
        notifyTurnComplete({
          sessionId,
          outcome: "done",
          onActivate: () => useRangerChatStore.getState().switchToSlot(sessionId),
        });
        return;
      }
      if (event === "contextCompressing") {
        // Count compactions as well as flagging one: a turn that compacts over
        // and over used to look exactly like a turn that compacted once, which
        // is how a stalling turn hid behind a plain spinner.
        const priorCount =
          useRangerChatStore.getState().slots.get(sessionId)?.compressionCount ?? 0;
        patchSlot(sessionId, {
          isCompressing: true,
          isEditingContext: false,
          compressionCount: priorCount + 1,
        });
        return;
      }
      // Within-turn tool-result eviction ("freeing memory") — distinct from
      // contextCompressing. String-compared because the generated
      // MessageStreamEvent enum only learns this value once schema codegen
      // runs; the wire value is correct regardless.
      if (String(event) === "contextEditing") {
        patchSlot(sessionId, { isEditingContext: true, isCompressing: false });
        return;
      }
      // The server is waiting to retry a model call; it says why and how often.
      if (event === "retrying") {
        patchSlot(sessionId, { retryNote: data.data?.rangerChatSubscription.messageStream.message ?? undefined });
        return;
      }
      patchSlot(sessionId, { isCompressing: false, isEditingContext: false, retryNote: undefined });
      const slot = useRangerChatStore.getState().slots.get(sessionId);
      if (slot?.shouldStop) return;
      setSlotChatHistory(sessionId, prev => applyStreamEventToHistory(prev, data));
    },
  });
  return null;
}
